import {
  type AuthenticatedUser,
  type UpdateProfileInput,
  updateProfileSchema,
} from '@discordo/shared';
import { useState, type FormEvent, type ReactElement } from 'react';

import { ApiError } from '../lib/api';

/**
 * The signed-in person's own name, and the small form behind it.
 *
 * The nickname is not a credential — the e-mail is — so changing it is an
 * everyday edit with nothing to re-authenticate. It stays collapsed until
 * clicked because the sidebar footer is a place people read far more often
 * than they edit.
 *
 * The component is pure: it takes the account and a save function, which keeps
 * it independent of the store and testable on its own.
 */

const GENERIC_FAILURE = 'Não foi possível salvar. Tente de novo.';

export interface ProfileEditorProps {
  user: AuthenticatedUser;
  onSave: (input: UpdateProfileInput) => Promise<void>;
}

export function ProfileEditor({ user, onSave }: ProfileEditorProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState(user.username);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function close(): void {
    setOpen(false);
    setError(null);
    // Anything typed and not saved is dropped: reopening shows what is true.
    setUsername(user.username);
    setDisplayName(user.displayName);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    // The same schema the API parses the body with, so a rejected nickname is
    // rejected here with the very sentence the server would have sent.
    const parsed = updateProfileSchema.safeParse({ username, displayName });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }

    setSaving(true);
    try {
      await onSave(parsed.data);
      setOpen(false);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        className="sidebar-user"
        type="button"
        title="Editar apelido"
        onClick={() => setOpen(true)}
      >
        <span className="avatar" aria-hidden="true">
          {user.displayName.slice(0, 1).toUpperCase()}
        </span>
        <span className="sidebar-user-name">{user.displayName}</span>
      </button>
    );
  }

  return (
    <form
      className="profile-editor"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {error !== null && (
        <p className="auth-alert" role="alert">
          {error}
        </p>
      )}

      <div className="auth-field">
        <label htmlFor="profile-username">Apelido</label>
        <input
          id="profile-username"
          name="username"
          type="text"
          autoFocus
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
      </div>

      <div className="auth-field">
        <label htmlFor="profile-display-name">Nome de exibição</label>
        <input
          id="profile-display-name"
          name="displayName"
          type="text"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>

      <div className="profile-editor-actions">
        <button className="auth-submit" type="submit" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
        <button className="sidebar-logout" type="button" onClick={close}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

export default ProfileEditor;
